/**
 * RaceTrace Live: alerts for the viewer's cars, on any tab of the live page (LiveAlerts.tsx shows
 * them). The cars are the focus car and the viewer's account cars in this race. From the live
 * field each second (useLiveField.ts) and the strategy (live-strategy.ts), an alert when:
 *
 *   - a caution comes out, the green comes back, a red flag, the checkered flag;
 *   - a car closes on one of the viewer's cars within URGENT_S (closing.ts), once per lap per pair;
 *   - one of the viewer's cars stops or goes off the track;
 *   - its pit window opens;
 *   - it goes into the pit lane, with where it should rejoin (pitNowFor, from before it went in).
 *
 * Each fires once per occurrence; stopped and off track not again for the same car within
 * REFIRE_MS, and stopped only once it has held for STOP_HOLD_MS (a stalled feed repeats a fix). The first look (on turning alerts on, or opening the page) only notes how things
 * stand: nothing fires for what was already so.
 */
import { lapsAhead } from "./lap-ahead";
import type { FlagChange } from "./live-field";
import type { Closing } from "./closing";
import { URGENT_S } from "./closing";
import type { PitNow, PitWindow } from "./live-strategy";

export type AlertKind = "flag" | "closing" | "stopped" | "offTrack" | "window" | "pit";
export const ALERT_KINDS: AlertKind[] = [
  "flag",
  "closing",
  "stopped",
  "offTrack",
  "window",
  "pit",
];
/** What each kind is, for the alert settings. */
export const ALERT_LABEL: Record<AlertKind, string> = {
  flag: "Caution, green, red and checkered flags",
  closing: `A car within ${URGENT_S} s of catching it`,
  stopped: "Stopped on track",
  offTrack: "Off the track",
  window: "Its pit window opens",
  pit: "It pits, and where it should rejoin",
};

export type AlertTone = "info" | "warn" | "urgent";

export interface LiveAlert {
  /** Unique per occurrence. */
  id: string;
  kind: AlertKind;
  /** The car it is about (chart key); null for a flag. */
  car: number | null;
  text: string;
  tone: AlertTone;
  at: number;
}

/** One look at the race, each second. */
export interface AlertLook {
  now: number;
  flagChange: FlagChange | null;
  /** The viewer's cars (chart keys). */
  cars: number[];
  /** The lap each is on (laps done + 1). */
  lap: Map<number, number>;
  /** The cars closing on each (closing.ts). */
  closing: Map<number, Closing[]>;
  stopped: Set<number>;
  offTrack: Set<number>;
  /** In the pit lane (chart keys). */
  inPit: Set<number>;
  window: Map<number, PitWindow | null>;
  /** Where each would rejoin if it stopped now. */
  pitNow: Map<number, PitNow>;
  /** "#120" for a chart key; its class for the rejoin text. */
  name: (key: number) => string;
  cls: (key: number) => string;
}

/** What the rules remember between looks. */
export interface AlertMemory {
  primed: boolean;
  flagAt: number | null;
  /** closing:<car>:<closer>:<lap>, stopped:<car>, offTrack:<car>, window:<car>:<from> → when it fired. */
  fired: Map<string, number>;
  /** Stopped since (client clock), and whether this stop has been told. */
  stoppedSince: Map<number, number>;
  stopTold: Set<number>;
  offTrack: Set<number>;
  inPit: Set<number>;
  lap: Map<number, number>;
  pitNow: Map<number, PitNow>;
  /** Its pit window is open now; its opening has been told since its last stop. */
  windowOpen: Set<number>;
  windowTold: Set<number>;
}

export const emptyMemory = (): AlertMemory => ({
  primed: false,
  flagAt: null,
  fired: new Map(),
  stoppedSince: new Map(),
  stopTold: new Set(),
  offTrack: new Set(),
  inPit: new Set(),
  lap: new Map(),
  pitNow: new Map(),
  windowOpen: new Set(),
  windowTold: new Set(),
});

/** Stopped or off track again for the same car within this: not a new alert. */
export const REFIRE_MS = 60_000;
/** Stopped for this long before it is told: a feed that stalls for a few seconds is not a stop. */
export const STOP_HOLD_MS = 10_000;
/** Fired keys are forgotten after this. */
const FORGET_MS = 30 * 60_000;

const FLAG_TEXT: Record<FlagChange["event"], [string, AlertTone]> = {
  caution: ["Caution", "urgent"],
  green: ["Green flag", "info"],
  red: ["Red flag", "urgent"],
  checkered: ["Checkered flag", "info"],
};

/** Its pit window is open on this lap. */
const isOpen = (w: PitWindow | null | undefined, lap: number | undefined) =>
  !!w && lap !== undefined && lap >= w.from && lap <= w.to;

export function alertsFor(
  mem: AlertMemory,
  look: AlertLook
): { alerts: LiveAlert[]; memory: AlertMemory } {
  const alerts: LiveAlert[] = [];
  const fired = new Map([...mem.fired].filter(([, at]) => look.now - at < FORGET_MS));
  const fire = (key: string, a: Omit<LiveAlert, "id" | "at">) => {
    fired.set(key, look.now);
    alerts.push({ ...a, id: `${key}@${look.now}`, at: look.now });
  };
  const recently = (key: string) => {
    const at = fired.get(key);
    return at !== undefined && look.now - at < REFIRE_MS;
  };
  const memory: AlertMemory = {
    primed: true,
    flagAt: look.flagChange?.at ?? mem.flagAt,
    fired,
    stoppedSince: new Map(
      [...look.stopped].map((k) => [k, mem.stoppedSince.get(k) ?? look.now])
    ),
    // A car stopped when alerts came on was not news; one no longer stopped can be again.
    stopTold: new Set(
      mem.primed ? [...mem.stopTold].filter((k) => look.stopped.has(k)) : look.stopped
    ),
    offTrack: new Set(look.offTrack),
    inPit: new Set(look.inPit),
    lap: new Map(look.lap),
    windowOpen: new Set(
      look.cars.filter((k) => isOpen(look.window.get(k), look.lap.get(k)))
    ),
    // A stop ends the stint: the next window is news again.
    windowTold: new Set(
      [...mem.windowTold].filter((k) => !(look.inPit.has(k) && !mem.inPit.has(k)))
    ),
    // Kept from before a car went in: once in, its rejoin is no longer "if it stopped now".
    pitNow: new Map([
      ...[...mem.pitNow].filter(([k]) => look.inPit.has(k)),
      ...[...look.pitNow].filter(([k]) => !look.inPit.has(k)),
    ]),
  };
  // The first look notes how things stand.
  if (!mem.primed) return { alerts, memory };

  const fc = look.flagChange;
  if (fc && fc.at !== mem.flagAt) {
    const [word, tone] = FLAG_TEXT[fc.event];
    fire(`flag:${fc.at}`, {
      kind: "flag",
      car: null,
      tone,
      text: fc.event === "caution" && fc.to ? `${word}: ${fc.to}` : word,
    });
  }

  for (const k of look.cars) {
    const n = look.name(k);
    const lap = look.lap.get(k) ?? 0;
    for (const c of look.closing.get(k) ?? []) {
      if (c.catchS > URGENT_S) continue;
      const key = `closing:${k}:${c.key}:${lap}`;
      if (!fired.has(key))
        fire(key, {
          kind: "closing",
          car: k,
          tone: "warn",
          text: `${look.name(c.key)} catches ${n} in ${Math.max(1, Math.round(c.catchS))} s`,
        });
    }
    // Stopped for STOP_HOLD_MS, once a stop.
    const since = memory.stoppedSince.get(k);
    if (
      since !== undefined &&
      look.now - since >= STOP_HOLD_MS &&
      !memory.stopTold.has(k) &&
      !recently(`stopped:${k}`)
    ) {
      memory.stopTold.add(k);
      fire(`stopped:${k}`, {
        kind: "stopped",
        car: k,
        tone: "urgent",
        text: `${n} stopped on track`,
      });
    }
    if (look.offTrack.has(k) && !mem.offTrack.has(k) && !recently(`offTrack:${k}`))
      fire(`offTrack:${k}`, {
        kind: "offTrack",
        car: k,
        tone: "warn",
        text: `${n} off the track`,
      });
    // Its window opening (not open on the last look), once a stint: the estimate moves lap by lap.
    const w = look.window.get(k);
    if (
      w &&
      isOpen(w, lap) &&
      !mem.windowOpen.has(k) &&
      !memory.windowTold.has(k) &&
      mem.lap.has(k)
    ) {
      memory.windowTold.add(k);
      fire(`window:${k}:${look.now}`, {
        kind: "window",
        car: k,
        tone: "info",
        text: `${n} pit window open: laps ${lapsAhead(w.from, w.to, lap - 1)}`,
      });
    }
    if (look.inPit.has(k) && !mem.inPit.has(k)) {
      const p = mem.pitNow.get(k);
      const rejoin = p
        ? `: should rejoin about P${p.rejoinPos} in ${look.cls(k)} (P${p.nowPos} before)`
        : "";
      fire(`pit:${k}:${look.now}`, {
        kind: "pit",
        car: k,
        tone: "info",
        text: `${n} in the pits${rejoin}`,
      });
    }
  }
  return { alerts, memory };
}

/** The alert settings, kept on this device: off until the viewer turns them on. */
export interface AlertSettings {
  on: boolean;
  /** The one-time prompt was answered. */
  asked: boolean;
  sound: boolean;
  vibrate: boolean;
  kinds: Record<AlertKind, boolean>;
}

const KEY = "rt.live.alerts";
export const DEFAULT_ALERTS: AlertSettings = {
  on: false,
  asked: false,
  sound: false,
  vibrate: true,
  kinds: {
    flag: true,
    closing: true,
    stopped: true,
    offTrack: true,
    window: true,
    pit: true,
  },
};

export function readAlertSettings(): AlertSettings {
  try {
    const v = JSON.parse(
      localStorage.getItem(KEY) ?? "null"
    ) as Partial<AlertSettings> | null;
    if (!v) return DEFAULT_ALERTS;
    const kinds = { ...DEFAULT_ALERTS.kinds };
    for (const k of ALERT_KINDS)
      if (typeof v.kinds?.[k] === "boolean") kinds[k] = v.kinds[k];
    return {
      on: v.on === true,
      asked: v.asked === true,
      sound: v.sound === true,
      vibrate: v.vibrate !== false,
      kinds,
    };
  } catch {
    return DEFAULT_ALERTS;
  }
}

export function saveAlertSettings(s: AlertSettings): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    /* private window: for this visit only */
  }
}
